// Calendar Week View Component (VAC-8)
// Horizontal timeline showing employee rows with vacation blocks

import React, { useMemo } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  addWeeks,
  subWeeks,
  isWithinInterval,
  isSameDay,
  isToday,
  parseISO
} from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { CalendarEntry } from '@/types/vacation'

interface CalendarWeekViewProps {
  currentWeek: Date
  vacationEntries: CalendarEntry[]
  onWeekChange: (newWeek: Date) => void
  isLoading?: boolean
}

export const CalendarWeekView: React.FC<CalendarWeekViewProps> = ({
  currentWeek,
  vacationEntries,
  onWeekChange,
  isLoading = false
}) => {
  const weekStart = startOfWeek(currentWeek, { locale: ptBR })
  const weekEnd = endOfWeek(currentWeek, { locale: ptBR })
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd })

  // Group entries by employee
  const employeeVacations = useMemo(() => {
    const map = new Map<string, CalendarEntry[]>()

    vacationEntries.forEach(entry => {
      if (!map.has(entry.employeeId)) {
        map.set(entry.employeeId, [])
      }
      map.get(entry.employeeId)!.push(entry)
    })

    return Array.from(map.entries()).map(([employeeId, entries]) => ({
      employeeId,
      employeeName: entries[0].employeeName,
      employeeAvatar: entries[0].employeeAvatar,
      vacations: entries
    }))
  }, [vacationEntries])

  const handlePrevWeek = () => {
    onWeekChange(subWeeks(currentWeek, 1))
  }

  const handleNextWeek = () => {
    onWeekChange(addWeeks(currentWeek, 1))
  }

  const handleCurrentWeek = () => {
    onWeekChange(new Date())
  }

  const isVacationOnDay = (vacation: CalendarEntry, day: Date): boolean => {
    // Fix timezone: use parseISO instead of new Date() to avoid timezone issues
    const vacationStart = parseISO(vacation.startDate)
    const vacationEnd = parseISO(vacation.endDate)
    return isWithinInterval(day, { start: vacationStart, end: vacationEnd })
  }

  return (
    <div>
      {/* Header with Navigation */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={handlePrevWeek}
            disabled={isLoading}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Semana anterior"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white min-w-[250px] text-center">
            {format(weekStart, "dd 'de' MMM", { locale: ptBR })} - {format(weekEnd, "dd 'de' MMM 'de' yyyy", { locale: ptBR })}
          </h2>
          <button
            onClick={handleNextWeek}
            disabled={isLoading}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Próxima semana"
          >
            <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
        <button
          onClick={handleCurrentWeek}
          disabled={isLoading}
          className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Semana Atual
        </button>
      </div>

      {/* Week Timeline Grid */}
      <div className="overflow-x-auto">
        <div className="min-w-[800px]">
          {/* Day Headers */}
          <div className="grid grid-cols-8 gap-2 mb-2">
            <div className="font-semibold text-gray-700 dark:text-gray-300 text-sm">
              Colaborador
            </div>
            {weekDays.map(day => {
              const dayKey = format(day, 'yyyy-MM-dd')
              return (
                <div
                  key={dayKey}
                  className={`text-center p-2 rounded ${
                    isToday(day)
                      ? 'bg-blue-100 dark:bg-blue-900/30 font-semibold'
                      : 'bg-gray-50 dark:bg-gray-800'
                  }`}
                >
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {format(day, 'EEE', { locale: ptBR })}
                  </div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {format(day, 'dd')}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Employee Rows */}
          {employeeVacations.length > 0 ? (
            <div className="space-y-2">
              {employeeVacations.map(({ employeeId, employeeName, employeeAvatar, vacations }) => (
                <div key={employeeId} className="grid grid-cols-8 gap-2">
                  {/* Employee Name Cell */}
                  <div className="flex items-center gap-2 p-2">
                    {employeeAvatar ? (
                      <img
                        src={employeeAvatar}
                        alt={employeeName}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center text-brand-dark text-xs font-semibold">
                        {employeeName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {employeeName}
                    </span>
                  </div>

                  {/* Day Cells */}
                  {weekDays.map(day => {
                    const vacationOnDay = vacations.find(v => isVacationOnDay(v, day))
                    const dayKey = format(day, 'yyyy-MM-dd')

                    return (
                      <div
                        key={`${employeeId}-${dayKey}`}
                        className={`p-2 rounded text-center ${
                          vacationOnDay
                            ? vacationOnDay.status === 'APPROVED'
                              ? 'bg-green-500 dark:bg-green-600 text-white'
                              : 'bg-yellow-500 dark:bg-yellow-600 text-white'
                            : isToday(day)
                            ? 'bg-blue-50 dark:bg-blue-900/20'
                            : 'bg-gray-50 dark:bg-gray-800'
                        }`}
                        title={
                          vacationOnDay
                            ? `${employeeName} - ${vacationOnDay.totalDays} dias (${vacationOnDay.status === 'APPROVED' ? 'Aprovada' : 'Pendente'})`
                            : undefined
                        }
                      >
                        {vacationOnDay && (
                          <div className="text-xs font-medium">
                            ✓
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-600 dark:text-gray-400">
              Nenhuma férias nesta semana
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 flex items-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-green-500 dark:bg-green-600" />
          <span className="text-gray-700 dark:text-gray-300">Aprovada</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-yellow-500 dark:bg-yellow-600" />
          <span className="text-gray-700 dark:text-gray-300">Pendente</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-blue-100 dark:bg-blue-900/30 border border-blue-500 dark:border-blue-400" />
          <span className="text-gray-700 dark:text-gray-300">Hoje</span>
        </div>
      </div>
    </div>
  )
}
